import * as React from 'react';
import { Box, Checkbox, Chip, FormControlLabel, IconButton, Skeleton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Tooltip, Typography } from '@mui/material';
import { FlexBoxInline, InputBaseSx, PrimaryBtnSx, SecondaryBtnSx } from '../../@shared/templates';
import { Palette, Sizes } from '../../@shared/palette';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { ButtonComponent } from '../../components/ButtonComponent';
import { usePersonalAccessToken } from '../../hooks/usePersonalAccessToken';
import moment from 'moment';


const SettingsTokens = () => {
  const [name, setName] = React.useState<string>('');
  const [isReadOnly, setIsReadOnly] = React.useState<boolean>(false);
  const [isCopied, setIsCopied] = React.useState<boolean>(false);
  const { onCreateToken, onRevokeToken, onLoadTokens, isProcessing, isLoading, tokens, newToken } = usePersonalAccessToken();

  const onCopy = React.useCallback(async () => {
    if (!newToken) return;
    navigator.clipboard.writeText(newToken);
    setIsCopied(true);
  }, [newToken]);

  React.useEffect(() => {
    onLoadTokens();
  }, [onLoadTokens]);

  if (isLoading) {
    return <Box sx={{ marginBottom: "10px", paddingTop: "20px" }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "20px" }}>
        <Skeleton variant="rectangular" width={300} height={40} sx={{ borderRadius: "4px" }} />
        <Skeleton variant="rectangular" width={100} height={20} />
        <Skeleton variant="rectangular" width={120} height={36} sx={{ borderRadius: "4px" }} />
      </Box>
      {newToken && <Skeleton variant="rectangular" width="100%" height={100} sx={{ borderRadius: "5px", marginBottom: "10px" }} />}
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              {[80, 80, 80, 70, 70, 70].map((w, i) => (
                <TableCell key={i}><Skeleton variant="text" width={w} sx={{ fontSize: Sizes.textSecondary }} /></TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {[0, 1, 2].map((row) => (
              <TableRow key={row}>
                <TableCell><Skeleton variant="text" width={120} /></TableCell>
                <TableCell><Skeleton variant="text" width={80} /></TableCell>
                <TableCell><Skeleton variant="text" width={80} /></TableCell>
                <TableCell><Skeleton variant="rectangular" width={70} height={24} sx={{ borderRadius: "16px" }} /></TableCell>
                <TableCell><Skeleton variant="rectangular" width={60} height={24} sx={{ borderRadius: "16px" }} /></TableCell>
                <TableCell><Skeleton variant="rectangular" width={60} height={30} sx={{ borderRadius: "4px" }} /></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  }

  return (
    <Box sx={{ marginBottom: "10px", paddingTop: "10px" }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "10px" }}>
        <TextField
          size='small'
          sx={{ ...InputBaseSx, maxWidth: "300px" }}
          label="Token name"
          variant="outlined"
          value={name}
          onChange={(e) => { setName(e.target.value); }}
        />
        <FormControlLabel
          sx={{ margin: "0", marginRight: "10px" }}
          control={<Checkbox checked={isReadOnly} onChange={(e) => setIsReadOnly(e.target.checked)} size="small" />}
          label={<Typography sx={{ color: Palette.textColorPrimary, fontSize: Sizes.textPrimary }}>Read only</Typography>}
        />
        <ButtonComponent
          text='Create token'
          disabled={!name.trim()}
          isProcessing={isProcessing}
          onClick={async() => {
            await onCreateToken(name, isReadOnly);
            setName('');
            setIsReadOnly(false);
          }}
          sx={PrimaryBtnSx}
        />
      </Box>

      {newToken && <Box sx={{ padding: "10px", borderRadius: "5px", background: Palette.backgroundColorPrimary, marginBottom: "10px" }}>
        <Box sx={FlexBoxInline}>
          <TextField fullWidth disabled size='small' sx={{ ...InputBaseSx }} label="New API Token" variant="outlined" value={newToken} />
          <Tooltip title={isCopied ? "Copied!" : "Copy"} placement="bottom" arrow>
            <IconButton type="button" sx={{ ...SecondaryBtnSx, color: Palette.textColorPrimary, p: "5px", border: "none" }} aria-label="copy" onClick={onCopy}>
              <ContentCopyIcon sx={{ fontSize: Sizes.icon }} />
            </IconButton>
          </Tooltip>
        </Box>
        <Typography sx={{ color: Palette.textColorPrimary, fontSize: Sizes.textSecondary }}>
          Copy this token now. You won't be able to see it again.
        </Typography>
      </Box>}

      {tokens.length > 0 && <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ color: Palette.textColorPrimary, fontSize: Sizes.textSecondary }}>Name</TableCell>
              <TableCell sx={{ color: Palette.textColorPrimary, fontSize: Sizes.textSecondary }}>Created</TableCell>
              <TableCell sx={{ color: Palette.textColorPrimary, fontSize: Sizes.textSecondary }}>Expires</TableCell>
              <TableCell sx={{ color: Palette.textColorPrimary, fontSize: Sizes.textSecondary }}>Access</TableCell>
              <TableCell sx={{ color: Palette.textColorPrimary, fontSize: Sizes.textSecondary }}>Status</TableCell>
              <TableCell sx={{ color: Palette.textColorPrimary, fontSize: Sizes.textSecondary }}></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {[...tokens].sort((a, b) => new Date(b.issuedAt).getTime() - new Date(a.issuedAt).getTime()).map((t) => (
              <TableRow key={t.id}>
                <TableCell sx={{ color: Palette.textColorPrimary, fontSize: Sizes.textPrimary }}>{t.name}</TableCell>
                <TableCell sx={{ color: Palette.textColorPrimary, fontSize: Sizes.textPrimary }}>{moment(t.issuedAt).format('MMMM Do YYYY, h:mm:ss a')}</TableCell>
                <TableCell sx={{ color: Palette.textColorPrimary, fontSize: Sizes.textPrimary }}>{moment(t.expiresAt).format('MMMM Do YYYY, h:mm:ss a')}</TableCell>
                <TableCell>
                  <Chip label={t.isReadOnly ? "Read only" : "Full access"} size="small" sx={{ fontSize: Sizes.textSecondary }} />
                </TableCell>
                <TableCell>
                  <Chip
                    label={t.isActive ? "Active" : "Revoked"}
                    size="small"
                    color={t.isActive ? "success" : "default"}
                    sx={{ fontSize: Sizes.textSecondary }}
                  />
                </TableCell>
                <TableCell>
                  {t.isActive && <ButtonComponent
                    text='Revoke'
                    isProcessing={isProcessing}
                    disabled={false}
                    onClick={() => onRevokeToken(t.id)}
                    sx={{ ...SecondaryBtnSx, fontSize: Sizes.textSecondary }}
                  />}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>}
    </Box>
  );
}

export default SettingsTokens;
